// @ts-strict-ignore
import {
	adClickTracker,
	AdSizeTracker,
	Apstag,
	BaseTrackingSetup,
	bidderTracker,
	communicationService,
	context,
	ctaTracker,
	DataWarehouseTracker,
	Dictionary,
	dsaHandler,
	eventsRepository,
	GAMOrigins,
	globalAction,
	InstantConfigService,
	interventionTracker,
	LabradorTracker,
	porvataTracker,
	PostmessageTracker,
	slotTracker,
	TrackingMessage,
	TrackingTarget,
	trackingUrls,
	viewabilityTracker,
} from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';
import { props } from 'ts-action';
import { DSA_MODAL_CLOSED, DSA_MODAL_OPENED } from '../../../communication/events/events-bidders';
import { VIDEO_PLAYER_TRACKING } from '../../../communication/events/events-video';

const adClickedAction = globalAction('[AdEngine] Ad clicked', props<Dictionary>());

@Injectable()
export class TrackingSetup extends BaseTrackingSetup {
	constructor(
		protected labradorTracker: LabradorTracker,
		protected dwTracker: DataWarehouseTracker,
		protected instantConfig: InstantConfigService,
		private adSizeTracker: AdSizeTracker,
	) {
		super(labradorTracker, dwTracker, instantConfig);
	}

	execute(): void {
		this.porvataTracker();
		this.slotTracker();
		this.identityTracker();
		this.viewabilityTracker();
		this.bidderTracker();
		this.postmessageTrackingTracker();
		this.interventionTracker();
		this.adClickTracker();
		this.ctaTracker();
		this.adSizeTracker.init();
		this.dsaModalTracker();
		super.execute();
	}

	public identityTracker(): void {
		if (
			!this.instantConfig.get('icIdentityPartners', false) &&
			context.get('options.trackingOptIn') &&
			!context.get('options.optOutSale')
		) {
			communicationService.on(
				eventsRepository.IDENTITY_PARTNER_DATA_OBTAINED,
				(eventInfo) => {
					const { partnerName, partnerIdentityId } = eventInfo.payload;
					// Track all partner ids except Google's as this one is already being tracked in identity engine
					if (partnerName !== 'Google') {
						this.dwTracker.track(
							{
								partner_name: partnerName,
								partner_identity_id: partnerIdentityId,
							},
							trackingUrls.IDENTITY_INFO,
						);
					}

					if (['liveConnect', 'MediaWiki-sha256'].includes(partnerName)) {
						const apstag = Apstag.make();
						apstag.init().then(() => apstag.sendHEM(partnerIdentityId));
					}
				},
				false,
			);
		}
	}

	private porvataTracker(): void {
		communicationService.on(
			VIDEO_PLAYER_TRACKING,
			({ eventInfo }) => {
				this.dwTracker.track(eventInfo, trackingUrls.AD_ENG_PLAYER_INFO);
			},
			false,
		);

		porvataTracker.register();
	}

	private slotTracker(): void {
		slotTracker.onChangeStatusToTrack.push('top-conflict');
		slotTracker.register(({ data }: Dictionary) => {
			this.dwTracker.track(data, trackingUrls.AD_ENG_AD_INFO);
		});
	}

	private viewabilityTracker(): void {
		viewabilityTracker.register(({ data }: Dictionary) => {
			this.dwTracker.track(data, trackingUrls.AD_ENG_VIEWABILITY);

			return data;
		});
	}

	private ctaTracker(): void {
		ctaTracker.register(({ data }: Dictionary) => {
			this.dwTracker.track(data, trackingUrls.AD_ENG_AD_INFO);
		});
	}

	private adClickTracker(): void {
		adClickTracker.register(({ data }: Dictionary) => {
			// event listeners might be outside of AdEngine, f.e. in the SilverSurfer interactions module
			communicationService.dispatch(adClickedAction(data));
			this.dwTracker.track(data, trackingUrls.AD_ENG_AD_INFO);
		});
	}

	private bidderTracker(): void {
		if (!context.get('bidders.prebid.enabled') && !context.get('bidders.a9.enabled')) {
			return;
		}

		bidderTracker.register(({ data }) => {
			this.dwTracker.track(data, trackingUrls.AD_ENG_BIDDERS);
		});
	}

	private postmessageTrackingTracker(): void {
		const postmessageTracker = new PostmessageTracker(['payload', 'target']);

		postmessageTracker.register<TrackingMessage>(
			async (message) => {
				const { target, payload } = message;

				switch (target) {
					case TrackingTarget.GoogleAnalytics: {
						window.ga(
							'tracker1.send',
							'event',
							payload.category,
							payload.action,
							payload.label,
							typeof payload.value === 'number' ? payload.value.toString() : payload.value,
						);
						break;
					}
					case TrackingTarget.DataWarehouse: {
						this.dwTracker.track(payload);
						break;
					}
					default:
						break;
				}

				return message;
			},
			[window.origin, ...GAMOrigins],
		);
	}

	private interventionTracker(): void {
		interventionTracker.register();
	}

	private dsaModalTracker(): void {
		if (!dsaHandler.isEnabled()) {
			return;
		}

		communicationService.on(
			DSA_MODAL_OPENED,
			({ adSlotName }) => {
				this.dwTracker.track({
					category: 'dsa-modal',
					action: 'click',
					label: 'open',
					value: `ad-slot-${adSlotName}`,
				});
			},
			false,
		);

		communicationService.on(
			DSA_MODAL_CLOSED,
			({ adSlotName }) => {
				this.dwTracker.track({
					category: 'dsa-modal',
					action: 'click',
					label: 'close',
					value: `ad-slot-${adSlotName}`,
				});
			},
			false,
		);
	}
}
